<template>
	<div
		ref="chatRef"
		class="support-chat-widget print:hidden overflow-hidden"
		:class="{
			'support-chat-widget__sidebar': isPinned,
			'support-chat-widget__floating': !isPinned,
			'opacity-0': !isWidgetVisible,
		}"
		:style="widgetStyle"
		tabindex="-1"
	>
		<div class="relative h-full w-full shrink-0 flex flex-col bg-shade-2">
			<!-- Sidebar drag handle -->
			<div v-if="!isMobile && isPinned" tabindex="-1" class="support-chat-widget__sidebar--handle absolute h-full" />

			<!-- Floating resize handles -->
			<template v-if="!isMobile && !isPinned">
				<div
					v-for="corner in ['tl','tr','bl','br']"
					:key="corner"
					tabindex="-1"
					class="support-chat-widget__floating--resize-handle absolute"
					:class="`support-chat-widget__floating--resize-handle-${corner}`"
				></div>
				<!-- Side handles for width resizing -->
				<div
					v-for="side in ['left','right']"
					:key="side"
					tabindex="-1"
					class="support-chat-widget__floating--resize-handle support-chat-widget__floating--resize-handle-side absolute"
					:class="`support-chat-widget__floating--resize-handle-${side}`"
				></div>
				<!-- Top/bottom handles for height resizing -->
				<div
					v-for="side in ['top','bottom']"
					:key="side"
					tabindex="-1"
					class="support-chat-widget__floating--resize-handle support-chat-widget__floating--resize-handle-side-vertical absolute"
					:class="`support-chat-widget__floating--resize-handle-${side}`"
				></div>
			</template>

			<!-- Content-->
			<div class="flex flex-col h-full w-full">
				<CognibotHeader
					:is-thinking="isThinking"
					:title="showHistory ? 'Chat History' : (!summary ? 'New Chat' : summary)"
					subtitle="Cognito Forms Support"
					:custom-class="!isPinned ? 'support-chat-widget__floating--move-handle' : ''"
				>
					<template #buttons>
						<div class="flex items-center">
							<CToolTip
								:use-button="false"
							>
								<template #button>
									<button aria-label="New Chat" class="support-chat-widget__button p-3 rounded-full text-icon-subtle flex button--no-style" @click="onNewChat">
										<Inline svg="plus" />
									</button>
								</template>
								<span>New Chat</span>
							</CToolTip>
							<CToolTip
								v-if="hasHistory"
								:use-button="false"
							>
								<template #button>
									<button aria-label="Chat History" class="support-chat-widget__button p-3 rounded-full text-icon-subtle flex button--no-style" @click="showHistoryPage">
										<Inline svg="audit-log" />
									</button>
								</template>
								<span>Chat History</span>
							</CToolTip>
							<CToolTip
								v-if="canPin"
								:use-button="false"
							>
								<template #button>
									<button :aria-label="isPinned ? 'Float Chat' : 'Dock Chat'" class="support-chat-widget__button p-3 rounded-full text-icon-subtle flex button--no-style" @click="togglePin">
										<Inline :svg="isPinned ? 'floating' : 'sidebar'" />
									</button>
								</template>
								<span>{{ isPinned ? 'Float' : 'Dock' }}</span>
							</CToolTip>
							<CToolTip
								:use-button="false"
							>
								<template #button>
									<button aria-label="Minimize Chat" class="support-chat-widget__button p-3 rounded-full text-icon-subtle flex button--no-style" @click="toggleOpen">
										<Inline :svg="isMobile ? 'chevron-down' : 'ex'" />
									</button>
								</template>
								<span>Minimize</span>
							</CToolTip>
						</div>
					</template>
				</CognibotHeader>

				<!-- @vue-ignore ref -->
				<ChatSupportContainer
					ref="supportChatContainer"
					:show-history="showHistory"
					:is-open="isWidgetVisible"
					@show-history="showHistory = $event"
					@update-chat-header="updateChatHeader"
					@has-history="hasHistory = $event"
					@is-thinking="onThinkingStatusChanged"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { computed, defineComponent, ref, inject, watch, nextTick, onUnmounted, onMounted } from 'vue';
	import { Draggable } from '@shopify/draggable';
	import { isMobileKey, navStateKey } from 'src/utilities/injection-keys';
	import ChatSupportContainer from 'src/components/app/SupportChatContainer.vue';
	import CognibotHeader from './CognibotHeader.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import { useRoute } from 'vue-router/composables';

	const INITIAL_RIGHT_POS = 24;
	const INITAL_BOTTOM_POS = 24;
	const INITIAL_FLOATING_WIDTH = 400;
	const INITIAL_FLOATING_HEIGHT = 600;
	const MIN_FLOATING_WIDTH = 300;
	const MIN_FLOATING_HEIGHT = 360;
	export const SUPPORT_CHAT_ANIMATION_DURATION = 300;

	export default defineComponent({
		components: {
			ChatSupportContainer,
			CToolTip,
			CognibotHeader
		},
		setup() {
			const isMobile = inject(isMobileKey);
			const navState = inject(navStateKey);

			const supportChatContainer = ref<InstanceType<typeof ChatSupportContainer> | null>(null);

			const appContainerHeight = ref<number | null>(null);
			const updateAppContainerHeight = () => {
				const appContainer = document.querySelector(
					'[data-app-layout].app-container, [data-app-settings-layout].app-settings'
				);
				appContainerHeight.value = appContainer ? appContainer.clientHeight : null;
			};

			const visualHeight = ref<number | null>(null);
			const visualBottom = ref(0);
			const updateVisualViewport = () => {
				const viewport = window.visualViewport;
				if (!viewport)
					return;

				visualHeight.value = viewport.height;
				visualBottom.value = Math.max(document.documentElement.clientHeight - viewport.offsetTop - viewport.height, 0);
			};

			const widgetStyle = computed(() => {
				if (!navState.isSupportPinned.value)
					return {};

				if (isMobile.value && navState.isSupportOpen.value && visualHeight.value)
					return { height: `${visualHeight.value}px`, bottom: `${visualBottom.value}px` };

				return appContainerHeight.value ? { height: `${appContainerHeight.value}px` } : {};
			});

			const isWidgetVisible = ref(navState.isSupportOpen.value);
			watch(navState.isSupportOpen, (isOpen) => {
				if (isOpen)
					isWidgetVisible.value = true;
				else
					setTimeout(() => (isWidgetVisible.value = navState.isSupportOpen.value), SUPPORT_CHAT_ANIMATION_DURATION);
			});

			const toggleOpen = () => navState.setSupportIsOpen(!navState.isSupportOpen.value);

			const togglePin = () => {
				updateAppContainerHeight();
				navState.setSupportIsPinned(!navState.isSupportPinned.value);
			};

			const sidebarWidth = ref(navState.supportWidth.value);
			watch(navState.supportWidth, (newWidth) => {
				requestAnimationFrame(() => {
					sidebarWidth.value = newWidth;
				});
			});

			const hasHistory = ref(false);
			const showHistory = ref(false);
			const showHistoryPage = () => {
				if (!showHistory.value) {
					showHistory.value = true;
				}
			};

			const summary = ref('');
			const conversationId = ref<string | null>(null);
			const updateChatHeader = (newName: string, id: string, checkId: boolean) => {
				if (checkId) {
					// Checks if the conversation changed before the summary finished generating
					if (id === conversationId.value) {
						summary.value = newName;
					}
				}
				else {
					// When loading a conversation, always update the ID and name
					conversationId.value = id;
					summary.value = newName;
				}
			};

			const onNewChat = () => {
				supportChatContainer.value?.onNewChat();
				summary.value = '';
				conversationId.value = null;
			};

			const isThinking = ref(false);
			const onThinkingStatusChanged = (thinking: boolean) => {
				isThinking.value = thinking;
			};

			// Utility to clamp a number between min and max
			const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

			const getInitialFloatingHeight = () => Math.min(INITIAL_FLOATING_HEIGHT, window.innerHeight * 0.9);

			// Initialize sidebar draggability
			const chatRef = ref<HTMLElement>(null);
			let sidebarDraggable: Draggable = null;
			let floatingDraggable: Draggable = null;

			const initializeSidebarDraggable = () => {
				sidebarDraggable = new Draggable(chatRef.value, {
					draggable: '.support-chat-widget__sidebar--handle'
				});

				// Update width on drag move; do not persist width to local storage
				sidebarDraggable.on('drag:move', e => navState.setSupportWidth(window.innerWidth - e.sensorEvent.clientX, false));

				// Persist width to local storage on drag stop
				sidebarDraggable.on('drag:stop', () => navState.setSupportWidth(navState.supportWidth.value, true));
			};

			// Floating position and size state
			const floatingRight = ref<number>(INITIAL_RIGHT_POS);
			const floatingBottom = ref<number>(INITAL_BOTTOM_POS);
			const floatingWidth = ref<number>(INITIAL_FLOATING_WIDTH);
			const floatingHeight = ref<number>(getInitialFloatingHeight());

			// TODO Get max floating height

			const initializeFloatingDraggable = () => {
				floatingDraggable = new Draggable(chatRef.value, {
					draggable: '.support-chat-widget__floating--move-handle, .support-chat-widget__floating--resize-handle',
					mirror: {
						appendTo: 'body',
						constrainDimensions: true
					}
				});

				let resizeCorner = null; // 'tl', 'tr', 'bl', 'br', or null for move
				let startOffsetX = 0;
				let startOffsetY = 0;
				let startWidth = 0;
				let startHeight = 0;
				let startX = 0;
				let startY = 0;
				let startRight = 0;
				let startBottom = 0;

				floatingDraggable.on('drag:start', (e) => {
					// Prevent dragging if the target is a button or inside a button
					const target = e.sensorEvent.target;
					if (target.closest('.support-chat-widget__button') || target.closest('button')) {
						e.cancel();
						return;
					}
					// Check which corner is being dragged
					resizeCorner = ['tl', 'tr', 'bl', 'br', 'left', 'right', 'top', 'bottom'].find(corner =>
						e.source.classList.contains(`support-chat-widget__floating--resize-handle-${corner}`)
					) || null;

					if (resizeCorner) {
						// Resize mode
						startWidth = floatingWidth.value;
						startHeight = floatingHeight.value;
						startX = e.sensorEvent.clientX;
						startY = e.sensorEvent.clientY;
						startRight = floatingRight.value;
						startBottom = floatingBottom.value;
					}
					else {
						// Move mode
						const rect = chatRef.value.getBoundingClientRect();
						startOffsetX = e.sensorEvent.clientX - rect.left;
						startOffsetY = e.sensorEvent.clientY - rect.top;
					}
				});

				floatingDraggable.on('drag:move', (e) => {
					if (resizeCorner) {
						// Handle resize based on which corner
						const deltaX = clamp(e.sensorEvent.clientX, 0, window.innerWidth) - startX;
						const deltaY = clamp(e.sensorEvent.clientY, 0, window.innerHeight) - startY;

						if (resizeCorner === 'br') {
							// Bottom-right: keep top-left fixed
							floatingWidth.value = Math.max(MIN_FLOATING_WIDTH, startWidth + deltaX);
							floatingHeight.value = Math.max(MIN_FLOATING_HEIGHT, startHeight + deltaY);
							floatingRight.value = startRight - (floatingWidth.value - startWidth);
							floatingBottom.value = startBottom - (floatingHeight.value - startHeight);
						}
						else if (resizeCorner === 'bl') {
							// Bottom-left: keep top-right fixed
							floatingWidth.value = Math.max(MIN_FLOATING_WIDTH, startWidth - deltaX);
							floatingHeight.value = Math.max(MIN_FLOATING_HEIGHT, startHeight + deltaY);
							floatingBottom.value = startBottom - (floatingHeight.value - startHeight);
						}
						else if (resizeCorner === 'tr') {
							// Top-right: keep bottom-left fixed
							floatingWidth.value = Math.max(MIN_FLOATING_WIDTH, startWidth + deltaX);
							floatingHeight.value = Math.max(MIN_FLOATING_HEIGHT, startHeight - deltaY);
							floatingRight.value = startRight - (floatingWidth.value - startWidth);
						}
						else if (resizeCorner === 'tl') {
							// Top-left: keep bottom-right fixed
							floatingWidth.value = Math.max(MIN_FLOATING_WIDTH, startWidth - deltaX);
							floatingHeight.value = Math.max(MIN_FLOATING_HEIGHT, startHeight - deltaY);
						}
						else if (resizeCorner === 'right') {
							// Right side: keep left edge fixed, only change width
							floatingWidth.value = Math.max(MIN_FLOATING_WIDTH, startWidth + deltaX);
							floatingRight.value = startRight - (floatingWidth.value - startWidth);
						}
						else if (resizeCorner === 'left') {
							// Left side: keep right edge fixed, only change width
							floatingWidth.value = Math.max(MIN_FLOATING_WIDTH, startWidth - deltaX);
						}
						else if (resizeCorner === 'bottom') {
							// Bottom side: keep top edge fixed, only change height
							floatingHeight.value = Math.max(MIN_FLOATING_HEIGHT, startHeight + deltaY);
							floatingBottom.value = startBottom - (floatingHeight.value - startHeight);
						}
						else if (resizeCorner === 'top') {
							// Top side: keep bottom edge fixed, only change height
							floatingHeight.value = Math.max(MIN_FLOATING_HEIGHT, startHeight - deltaY);
						}
					}
					else {
						// Handle move
						const rect = chatRef.value.getBoundingClientRect();

						// Calculate new left/top position from mouse
						const newLeft = e.sensorEvent.clientX - startOffsetX;
						const newTop = e.sensorEvent.clientY - startOffsetY;

						// Keep widget within viewport bounds
						const boundedLeft = clamp(newLeft, 0, window.innerWidth - rect.width);
						const boundedTop = clamp(newTop, 0, window.innerHeight - rect.height);

						// Convert to right/bottom positioning
						floatingRight.value = window.innerWidth - boundedLeft - rect.width;
						floatingBottom.value = window.innerHeight - boundedTop - rect.height;
					}
				});
			};

			const resetFloatingPosition = () => {
				floatingRight.value = INITIAL_RIGHT_POS;
				floatingBottom.value = INITAL_BOTTOM_POS;
				floatingWidth.value = INITIAL_FLOATING_WIDTH;
				floatingHeight.value = getInitialFloatingHeight();
			};

			const ensureFloatingInViewport = () => {
				const overflowRight = floatingRight.value + floatingWidth.value > window.innerWidth;
				const overflowBottom = floatingBottom.value + floatingHeight.value > window.innerHeight;
				const overflowLeft = floatingRight.value < 0;
				const overflowTop = floatingBottom.value < 0;

				// If any overflow, reset position to defaults
				if (overflowRight || overflowBottom || overflowLeft || overflowTop)
					resetFloatingPosition();
			};

			onMounted(() => {
				updateAppContainerHeight();
				updateVisualViewport();
				window.addEventListener('resize', updateAppContainerHeight);
				window.visualViewport?.addEventListener('resize', updateVisualViewport);
				window.visualViewport?.addEventListener('scroll', updateVisualViewport);
			});

			onUnmounted(() => {
				window.removeEventListener('resize', updateAppContainerHeight);
				window.visualViewport?.removeEventListener('resize', updateVisualViewport);
				window.visualViewport?.removeEventListener('scroll', updateVisualViewport);
			});

			const route = useRoute();
			watch(() => route.fullPath, () => {
				updateAppContainerHeight();
			});

			watch([navState.isSupportOpen, navState.isSupportPinned, isMobile], ([isOpened, isPinned, isMobile]) => {
				nextTick(() => {
					if (!isOpened || isMobile) {
						sidebarDraggable?.destroy();
						floatingDraggable?.destroy();
						window.removeEventListener('resize', ensureFloatingInViewport);

						// Reset floating position and size
						setTimeout(resetFloatingPosition, SUPPORT_CHAT_ANIMATION_DURATION);

						return;
					}

					if (isPinned) {
						floatingDraggable?.destroy();
						initializeSidebarDraggable();
						window.removeEventListener('resize', ensureFloatingInViewport);
					}
					else {
						sidebarDraggable?.destroy();
						initializeFloatingDraggable();
						window.addEventListener('resize', ensureFloatingInViewport);
						nextTick(ensureFloatingInViewport);
					}
				});
			}, { immediate: true });

			onUnmounted(() => (window.removeEventListener('resize', ensureFloatingInViewport)));
			return {
				isMobile,
				isWidgetVisible,
				toggleOpen,
				togglePin,
				hasHistory,
				showHistory,
				showHistoryPage,
				summary,
				updateChatHeader,
				onNewChat,
				onThinkingStatusChanged,
				isThinking,
				supportChatContainer,
				chatRef,
				chatWidth: navState.supportWidth,
				isPinned: navState.isSupportPinned,
				canPin: navState.canPinSupport,
				floatingRight,
				floatingBottom,
				floatingWidth,
				floatingHeight,
				appContainerHeight,
				widgetStyle,
				SUPPORT_CHAT_ANIMATION_DURATION
			};
		}
	});
</script>

<style lang="scss">
// Disable animations while dragging or when the support panel is closed, except during open/close transitions
.draggable--is-dragging .support-chat-widget,
.admin:not(.admin--support-animates) .support-chat-widget {
	transition: none;
}

// Closed states
.admin:not(.admin--support-open) {

	&:not(.admin--mobile) .support-chat-widget__sidebar {
		right: calc(-1 * var(--support-width));
	}

	&.admin--mobile .support-chat-widget {
		bottom: -100vh;
	}

	.support-chat-widget__floating {
		opacity: 0;
		transform: translateY(calc(100% + 24px));
		pointer-events: none;
	}
}

.admin--mobile .support-chat-widget {
	transition: bottom v-bind('SUPPORT_CHAT_ANIMATION_DURATION + "ms"') ease-in-out;
}

$z-chat: 12;
$z-drag-handle: 13;
$z-drag-handle-corner: 14;

.support-chat-widget {
	z-index: $z-chat;
	border: 1px solid var(--border-primary);
	transition-duration: v-bind('SUPPORT_CHAT_ANIMATION_DURATION + "ms"');
	transition-property:
		left,
		right,
		top,
		bottom,
		width,
		height,
		box-shadow,
		transform,
		opacity;
	transition-timing-function: ease-in-out;
	container-type: inline-size;

	// Floating state
	&__floating {
		position: fixed;
		bottom: v-bind('floatingBottom + "px"');
		right: v-bind('floatingRight + "px"');
		width: v-bind('floatingWidth + "px"');
		height: v-bind('floatingHeight + "px"');
		box-shadow: 0 4px 20px rgba(0, 0, 0, 15%);
		opacity: 1;
		pointer-events: auto;

		&--move-handle {
			cursor: move;
			user-select: none;

			&.draggable-source--is-dragging {
				cursor: grabbing;
			}
		}

		&--resize-handle {
			position: absolute;
			z-index: $z-drag-handle;
			width: 20px;
			height: 20px;
			transition: background-color .2s .2s, opacity .3s ease-in-out;

			&-tl {
				position: absolute;
				left: 0;
				top: 0;
				z-index: $z-drag-handle-corner;
				cursor: nwse-resize;
			}

			&-tr {
				position: absolute;
				right: 0;
				top: 0;
				z-index: $z-drag-handle-corner;
				cursor: nesw-resize;
			}

			&-bl {
				position: absolute;
				bottom: 0;
				left: 0;
				z-index: $z-drag-handle-corner;
				cursor: nesw-resize;
			}

			&-br {
				position: absolute;
				bottom: 0;
				right: 0;
				z-index: $z-drag-handle-corner;
				cursor: nwse-resize;
			}

			&-side {
				top: 0;
				width: 5px;
				height: 100%;

				&.draggable-source--is-dragging,
				&:hover {
					background: var(--accent-5);
				}
			}

			&-side-vertical {
				left: 0;
				width: 100%;
				height: 5px;

				&.draggable-source--is-dragging,
				&:hover {
					background: var(--accent-5);
				}
			}

			&-left {
				left: 0;
				cursor: ew-resize;
			}

			&-right {
				right: 0;
				cursor: ew-resize;
			}

			&-top {
				top: 0;
				cursor: ns-resize;
			}

			&-bottom {
				bottom: 0;
				cursor: ns-resize;
			}
		}
	}

	// Docked/sidebar state
	&__sidebar {
		position: fixed;
		bottom: 0;
		right: 0;
		width: var(--support-width);
		height: 100dvh;
		border-left: 1px solid var(--border-primary);

		&--handle {
			z-index: $z-drag-handle;
			width: 5px;
			outline: 0;
			cursor: col-resize;
			opacity: 1;
			transition: background-color .2s .2s, opacity .3s ease-in-out;
			pointer-events: auto;

			&.draggable-source--is-dragging,
			&:hover {
				background: var(--accent-5);
			}

			@include b-down($b9) {
				left: -8px;
				width: 12px;

				&.draggable-source--is-dragging,
				&:hover {
					background: linear-gradient(to left, transparent, transparent 4px, var(--accent-5) 4px, var(--accent-5) 9px, transparent 9px);
				}
			}
		}
	}

	&__button {
		padding: $space__3;

		@include b-up($b6) {
			padding: 0;
		}

		&:hover {
			background: var(--accent-4);
			transition: background .3s .1s;

			:root & i {
				color: var(--icon-intense);
				transition: color .3s .1s;
			}
		}
	}

	&__header {
		height: 60px;
		background: var(--accent-3);
		border-bottom: 1px solid var(--border-primary);

		[data-color-scheme='dark'][data-has-custom-theme='false'] & {
			background: var(--bg-primary);
		}
	}

	.cognito-ai {
		position: relative;
		top: 25px;
		width: 125px;
		height: 125px;
		scale: 1.1;
	}
}

.draggable-mirror .cognito-ai {
	display: none !important;
}

[document-has='task-dashboard'],
[document-has='cognito-pay'] {

	.admin--mobile .support-chat-widget {
		display: none;
	}
}
</style>
